import { useEffect, useState, type FormEvent } from "react";
import { useLantern, useSession } from "../hooks";
import type { Delegation } from "../sync/types";
import { setSound, soundOn, tryChime } from "../sound";
import { popUpsOn, setPopUps } from "../popups";
import { Badge, Card, EmptyState, Field, Form, Page, Switch } from "./ui";
import { AccountCards, ProfilePage, SessionsCards } from "./MePages";
import { SecurityCards } from "./Security";
import { QuietHoursCard } from "./QuietHoursCard";
import { AgentList, NewAgentButton, useAgents } from "./AgentTokens";
import { APITokensPage } from "./APITokens";
import { chosen, languages, t } from "../i18n";
import { densityInUse, layoutInUse, textSize, textSizes, type Density, type Layout, type TextSize } from "../display";
import { appearance, ownTheme, workspaceThemeID, type Appearance } from "../theme";
import { ThemePicker } from "./ThemePicker";
import { locale } from "../i18n";

const scopeLabels = (): Record<string, string> => ({
  "channels:read": t("Read channels"),
  "chat:write": t("Post messages"),
});

// Settings are a person's own, a page each (the settings sidebar lists
// them): their profile; language and theme; how they are notified; the
// delegations they granted agents, which they can revoke at once
// (ADR-0007), and the agents they own; their API tokens and their agents';
// their account, how they sign in, and where they are signed in.
export function Settings({ page, onSignOut }: { page: string; onSignOut: () => void }) {
  switch (page) {
    case "profile":
      return <ProfilePage />;
    case "notifications":
      return <NotificationsPage />;
    case "agents":
      return <AgentsPage />;
    case "tokens":
      return <APITokensPage />;
    case "account":
      return (
        <Page className="settings" narrow title={t("Account")} description={t("Who you sign in as, and your password.")}>
          <AccountCards />
        </Page>
      );
    case "security":
      return (
        <Page className="settings" narrow title={t("Security")} description={t("How you prove it is you when you sign in: passkeys and two-step verification.")}>
          <SecurityCards />
        </Page>
      );
    case "sessions":
      return (
        <Page className="settings" narrow title={t("Sessions")} description={t("The browsers and devices signed in as you.")}>
          <SessionsCards onSignOut={onSignOut} />
        </Page>
      );
  }
  return (
    <Page className="settings" narrow title={t("Preferences")} description={t("Your language, theme, and text size and spacing, on all your devices.")}>
      <LanguageCard />
      <ThemeCard />
      <DisplayCard />
      <p className="muted page-footnote">
        <a href="/third-party-licenses.txt" target="_blank" rel="noopener noreferrer">
          {t("Open-source licenses")}
        </a>
      </p>
    </Page>
  );
}

function NotificationsPage() {
  const [sound, setSoundState] = useState(soundOn);
  const [popUps, setPopUpsState] = useState(popUpsOn);
  const [permission, setPermission] = useState<string>(typeof Notification === "undefined" ? "unsupported" : Notification.permission);
  const desktop =
    permission === "granted"
      ? { tone: "ok" as const, label: t("On"), text: t("You get desktop notifications while Lantern is in the background.") }
      : permission === "denied"
        ? { tone: "bad" as const, label: t("Blocked"), text: t("Blocked in this browser's site settings; allow them there to turn them on.") }
        : !window.isSecureContext || permission === "unsupported"
          ? { tone: "neutral" as const, label: t("Unavailable"), text: t("This browser allows desktop notifications only over HTTPS.") }
          : { tone: "warn" as const, label: t("Off"), text: t("Get a notification for DMs and mentions while Lantern is in the background.") };

  return (
    <Page className="settings" narrow title={t("Notifications")}>
      <Card
        title={t("Notifications")}
        description={t("Direct messages and mentions of you show as a pop-up and as a count in the tab's title. Pop-ups, sound and desktop notifications are set for this browser; quiet hours for all your devices.")}
      >
        <Switch
          checked={sound}
          onChange={(on) => {
            setSound(on);
            setSoundState(on);
          }}
          label={t("Play a sound for direct messages and mentions")}
          description={t("A short chime, at most once in a few seconds.")}
          extra={
            <button type="button" className="ghost" onClick={tryChime}>
              {t("Play it")}
            </button>
          }
        />
        <Switch
          checked={popUps}
          onChange={(on) => {
            setPopUps(on);
            setPopUpsState(on);
          }}
          label={t("Show a pop-up for direct messages and mentions")}
          description={t("In the corner of the page, while Lantern is in front. Reminders and missed huddles still show one.")}
        />
        <KeywordsField />
        <div className="switch-row">
          <span className="switch-text">
            <span className="switch-label">
              {t("Desktop notifications")} <Badge tone={desktop.tone}>{desktop.label}</Badge>
            </span>
            <span className="field-hint">{desktop.text}</span>
          </span>
          {permission === "default" && window.isSecureContext && (
            <button className="small" onClick={() => void Notification.requestPermission().then(setPermission)}>
              {t("Allow")}
            </button>
          )}
        </div>
      </Card>

      <QuietHoursCard />
    </Page>
  );
}

// AgentsPage is what agents may do in one's name, and the agents one owns.
// Admins see every agent in Administration.
function AgentsPage() {
  const session = useSession();
  const state = useLantern();
  const me = state.me?.id;
  const [delegations, setDelegations] = useState<Delegation[]>([]);
  const [agent, setAgent] = useState("");
  const [scopes, setScopes] = useState<string[]>(["channels:read"]);
  const [channels, setChannels] = useState<string[]>([]);
  const [days, setDays] = useState(30);
  const [error, setError] = useState("");

  const load = () => session.api.delegations().then((r) => setDelegations(r.delegations), () => setError(t("Could not load delegations.")));
  useEffect(() => {
    void load();
  }, [session]);

  const agents = [...session.directory.values()].filter((p) => p.kind === "agent" && p.status === "active");
  const mine = delegations.filter((d) => d.root_grantor_id === me);
  // Owners can look into what their agents did, also ones no longer active.
  const [owned, reload] = useAgents(false);

  async function grant(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await session.api.delegate(agent, scopes, channels, days);
      setChannels([]);
      setAgent("");
      await load();
    } catch {
      setError(t("Could not grant the delegation."));
    }
  }

  async function revoke(id: string) {
    await session.api.revokeDelegation(id).catch(() => setError(t("Could not revoke.")));
    await load();
  }

  return (
    <Page className="settings" narrow title={t("Agents")} description={t("What agents may do in your name, and the agents you own.")}>
      {error && <p className="error">{error}</p>}
      <Card title={t("Agents acting for you")} description={t("Each of these agents can act as you, within what you allowed, until it expires or you revoke it.")}>
        {mine.length === 0 ? (
          <EmptyState icon="🔐" title={t("No agent can act for you")}>
            {t("Grant an agent access below to let it read or post as you.")}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("Agent")}</th>
                  <th>{t("Can")}</th>
                  <th>{t("Channels")}</th>
                  <th>{t("Until")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {mine.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <strong>{session.name(d.grantee_id)}</strong>
                      {d.depth > 1 && <span className="row-meta">{t("passed on by {name}", { name: session.name(d.grantor_id) })}</span>}
                    </td>
                    <td>
                      <span className="badges">
                        {d.scopes.map((s) => (
                          <Badge key={s} tone="accent">
                            {scopeLabels()[s] ?? s}
                          </Badge>
                        ))}
                      </span>
                    </td>
                    <td>{d.channel_ids.length === 0 ? t("All yours") : d.channel_ids.map((c) => session.channelName(c)).join(", ")}</td>
                    <td>{new Date(d.expires_at).toLocaleDateString(locale)}</td>
                    <td className="num">
                      <button className="small danger" onClick={() => revoke(d.id)}>
                        {t("Revoke")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title={t("Let an agent act for you")} description={t("Choose what it may do, where, and for how long. You can revoke it at any time.")}>
        <Form onSubmit={grant}>
          <Field label={t("Agent")}>
            <select value={agent} onChange={(e) => setAgent(e.target.value)} required>
              <option value="">{t("Choose an agent…")}</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.display_name}
                </option>
              ))}
            </select>
          </Field>
          <fieldset className="checks">
            <legend>{t("It may")}</legend>
            {Object.entries(scopeLabels()).map(([scope, label]) => (
              <label key={scope} className="check-row">
                <input
                  type="checkbox"
                  checked={scopes.includes(scope)}
                  onChange={(e) => setScopes(e.target.checked ? [...scopes, scope] : scopes.filter((s) => s !== scope))}
                />
                {label}
              </label>
            ))}
          </fieldset>
          <div className="inline-fields">
            <Field label={t("Only these channels")} hint={t("Select none for all your channels.")}>
              <select multiple value={channels} onChange={(e) => setChannels(Array.from(e.target.selectedOptions, (o) => o.value))}>
                {[...state.channels.values()].map((c) => (
                  <option key={c.channel.id} value={c.channel.id}>
                    {session.channelName(c.channel.id)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t("For how many days")} hint={t("Between 1 and 90.")}>
              <input type="number" min={1} max={90} value={days} onChange={(e) => setDays(Number(e.target.value))} />
            </Field>
          </div>
          <div className="form-actions">
            <button type="submit" disabled={!agent || scopes.length === 0}>
              {t("Grant")}
            </button>
          </div>
        </Form>
      </Card>

      <Card
        title={t("Your agents")}
        description={t("Agents you own: give them tokens to sign in with, or open one's traces to see what it read and did.")}
        actions={<NewAgentButton onMade={reload} />}
      >
        {owned.length === 0 ? (
          <EmptyState icon="🤖" title={t("You own no agents")} />
        ) : (
          <AgentList agents={owned} onChanged={reload} />
        )}
      </Card>
    </Page>
  );
}

// KeywordsField sets the words that notify one as a mention would, on all
// one's devices.
function KeywordsField() {
  const session = useSession();
  const state = useLantern();
  const saved = state.keywords.join(", ");
  const [text, setText] = useState(saved);
  const [said, setSaid] = useState("");
  const [error, setError] = useState("");
  useEffect(() => setText(saved), [saved]);
  async function save(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSaid("");
    try {
      await session.setKeywords(text.split(/[,，\n]/));
      setSaid(t("Saved."));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  return (
    <Form className="keywords-field" onSubmit={save}>
      <Field label={t("My keywords")} hint={t("Messages that say any of these notify you as a mention would, in channels you have not muted. Separate them with commas.")}>
        <div className="keywords-row">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("release, outage, on-call")} aria-label={t("My keywords")} />
          <button type="submit" disabled={text === saved}>
            {t("Save")}
          </button>
        </div>
      </Field>
      {error && <p className="error">{error}</p>}
      {said && <p className="notice">{said}</p>}
    </Form>
  );
}

// LanguageCard sets the language one reads Lantern in, on every device.
function LanguageCard() {
  const session = useSession();
  const [value, setValue] = useState(chosen);
  const [error, setError] = useState("");
  return (
    <Card title={t("Language")} description={t("The language of Lantern's menus and messages, on all your devices. Changing it reloads the page.")}>
      <Field label={t("Language")}>
        <select
          value={value}
          onChange={(e) => {
            setError("");
            setValue(e.target.value);
            session.setLanguage(e.target.value).catch((err) => setError(err instanceof Error ? err.message : String(err)));
          }}
        >
          <option value="">{t("Automatic: your browser's language")}</option>
          {languages.map((l) => (
            <option key={l.id} value={l.id} lang={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </Field>
      {error && <p className="error">{error}</p>}
    </Card>
  );
}

// DisplayCard sets how large Lantern's text is, how close together
// messages and lists sit, and how messages are laid out, for all one's
// devices.
function DisplayCard() {
  const session = useSession();
  const [size, setSize] = useState(textSize);
  const [density, setDensity] = useState(densityInUse);
  const [layout, setLayout] = useState(layoutInUse);
  const [error, setError] = useState("");
  const save = (s: TextSize, d: Density, l: Layout = layout) => {
    setSize(s);
    setDensity(d);
    setLayout(l);
    setError("");
    session.setDisplay(s, d, l).catch((err) => setError(err instanceof Error ? err.message : String(err)));
  };
  const layouts: [Layout, string, string][] = [
    ["", t("Standard"), t("Everyone's messages in one column, with their names, as most work chat apps have them.")],
    ["bubbles", t("Bubbles"), t("Yours on the right and others' on the left, in bubbles, as most phone chat apps have them.")],
  ];
  const sizeNames: Record<TextSize, string> = { small: t("Small"), "": t("Default"), large: t("Large"), larger: t("Larger") };
  const densities: [Density, string, string][] = [
    ["", t("Comfortable"), t("Room around each message, and its sender's picture full-size.")],
    ["compact", t("Compact"), t("More messages on screen, closer together.")],
  ];
  return (
    <Card title={t("Text and spacing")} description={t("How large text is and how much fits on screen, on all your devices.")}>
      <h3 className="theme-heading">{t("Text size")}</h3>
      <div className="theme-choices" role="radiogroup" aria-label={t("Text size")}>
        {textSizes.map(([id, scale]) => (
          <label key={id || "default"} className="theme-choice size-choice">
            <input type="radio" name="text-size" checked={size === id} onChange={() => save(id, density)} />
            <span className="theme-swatch" aria-hidden>
              <span style={{ fontSize: `${Math.round(22 * scale)}px` }}>Aa</span>
            </span>
            {sizeNames[id]}
          </label>
        ))}
      </div>
      <h3 className="theme-heading">{t("Density")}</h3>
      <div className="theme-choices" role="radiogroup" aria-label={t("Density")}>
        {densities.map(([id, name, about]) => (
          <label key={id || "comfortable"} className={`theme-choice density-choice ${id || "comfortable"}`} title={about}>
            <input type="radio" name="density" checked={density === id} onChange={() => save(size, id)} />
            <span className="theme-swatch" aria-hidden>
              {Array.from({ length: id ? 4 : 3 }, (_, i) => (
                <i key={i} />
              ))}
            </span>
            {name}
          </label>
        ))}
      </div>
      <h3 className="theme-heading">{t("Messages")}</h3>
      <div className="theme-choices" role="radiogroup" aria-label={t("Messages")}>
        {layouts.map(([id, name, about]) => (
          <label key={id || "standard"} className={`theme-choice layout-choice ${id || "standard"}`} title={about}>
            <input type="radio" name="message-layout" checked={layout === id} onChange={() => save(size, density, id)} />
            <span className="theme-swatch" aria-hidden>
              <i />
              <i className="mine" />
              <i />
            </span>
            {name}
          </label>
        ))}
      </div>
      {error && <p className="error">{error}</p>}
    </Card>
  );
}

// ThemeCard sets light or dark, or the system's, and the color theme, or
// the workspace's, for all one's devices.
function ThemeCard() {
  const session = useSession();
  const [mode, setMode] = useState(appearance);
  const [own, setOwn] = useState(ownTheme);
  const [error, setError] = useState("");
  const shown = (document.documentElement.dataset.mode === "dark" ? "dark" : "light") as "light" | "dark";
  const save = (theme: string, a: Appearance) => {
    setOwn(theme);
    setMode(a);
    setError("");
    session.setTheme(theme, a).catch((err) => setError(err instanceof Error ? err.message : String(err)));
  };
  const options: [Appearance, string][] = [
    ["", t("System: follow this device")],
    ["light", t("Light")],
    ["dark", t("Dark")],
  ];
  return (
    <Card title={t("Theme")} description={t("How Lantern looks, on all your devices.")}>
      <div className="theme-choices" role="radiogroup" aria-label={t("Light or dark")}>
        {options.map(([v, label]) => (
          <label key={v || "system"} className={`theme-choice ${v || "system"}`}>
            <input type="radio" name="theme" checked={mode === v} onChange={() => save(own, v)} />
            <span className="theme-swatch" aria-hidden />
            {label}
          </label>
        ))}
      </div>
      <h3 className="theme-heading">{t("Color theme")}</h3>
      <ThemePicker value={own} onChange={(id) => save(id, mode)} workspace={workspaceThemeID()} mode={shown} />
      {error && <p className="error">{error}</p>}
    </Card>
  );
}
